import React, { useEffect, useRef } from 'react';
import {
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { initialValues, validationSchema } from './constants';

const toolbarOptions = [
  [{ font: [] }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike'], // toggled buttons
  [{ color: [] }, { background: [] }], // dropdown with defaults from theme
  [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
  [{ direction: 'rtl' }], // text direction
  ['blockquote', 'code-block'],
  ['link', 'video'],
  ['clean'], // remove formatting button
];

function NewQuestion({ isOpen, toggle, header, addQuestionMutation }) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const inputRef = useRef(null); // 1. Create ref for Input

  // 2. Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      reset(initialValues); // Reset form values and validation state
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [isOpen, reset]);

  const onSubmit = formData => {
    const payload = {
      headerId: header?.HeaderId,
      question: formData.question,
      answer: formData.answer,
    };
    addQuestionMutation.mutate(payload);
    reset();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="xl"
      onOpened={() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }}
    >
      <ModalHeader toggle={toggle}>
        Add New Question & Answer Under {header?.Title}
      </ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <div className="d-flex flex-column gap-3">
            <div>
              <Label>
                Question<span className="text-danger">*</span>
              </Label>
              <Controller
                name="question"
                control={control}
                render={({ field }) => (
                  <Input
                    innerRef={inputRef} // <-- pass the ref here using innerRef for reactstrap Input
                    {...field}
                    placeholder="Enter your question here"
                    type="text"
                    className={`form-control external-filter-input ${errors.question ? 'is-invalid' : ''}`}
                  />
                )}
              />
              {errors.question && (
                <div className="invalid-feedback">
                  {errors.question.message}
                </div>
              )}
            </div>
            <div>
              <Label>
                Answer<span className="text-danger">*</span>
              </Label>
              <Controller
                name="answer"
                control={control}
                render={({ field }) => (
                  <div
                    className={`rich-editor-wrapper ${errors.answer ? 'is-invalid' : ''}`}
                  >
                    <ReactQuill
                      {...field}
                      theme="snow"
                      placeholder="Enter your answer here"
                      onChange={field.onChange}
                      value={field.value}
                      modules={{ toolbar: toolbarOptions }}
                    />
                  </div>
                )}
              />
              {errors.answer && (
                <div className="invalid-feedback">{errors.answer.message}</div>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-start">
          <button
            disabled={!isValid}
            type="submit"
            className="custom-secondary-btn"
          >
            Add
          </button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default NewQuestion;
